import { useCallback, useEffect, useMemo, useState } from 'react'
import OrganizationIssuesService from '../../../services/api/OrganizationIssuesService'
import { groupByCategory, toStatus } from './useIssuesByStatus'

const INITIAL_PAGY = { page: 1, pages: 0, count: 0, items: 0 }
const noop = () => null

// One GET /issues.json feeds the table, the stats cards and the StatusFilter.
// Returns the same values TriggeredAlertsTab already consumes from its per-collect hooks.
const useOrganizationAlerts = ({
  page, items, search, collectIds, steps, content, sort, direction, columns, variables,
  startDate, endDate, enabled = true
}) => {
  const [issues, setIssues] = useState([])
  const [statsRows, setStatsRows] = useState([])
  const [stepRows, setStepRows] = useState([])
  const [pagy, setPagy] = useState(INITIAL_PAGY)
  const [loading, setLoading] = useState(true)
  const [hasLoaded, setHasLoaded] = useState(false)
  const [error, setError] = useState(null)
  const [refetchToken, setRefetchToken] = useState(0)

  const refetch = useCallback(() => setRefetchToken(token => token + 1), [])
  const mergeIssue = useCallback(updated => {
    if (!updated?.id)
      return
    setIssues(previous => previous.map(issue =>
      issue.id === updated.id ? { ...issue, ...updated } : issue))
  }, [])

  const collectIdsKey = (collectIds || []).join(',')
  const stepsKey = (steps || []).join(',')
  const contentKey = (content || []).join(',')
  const columnsKey = columns ? columns.join(',') : ''
  const variablesKey = variables ? variables.join(',') : ''

  useEffect(() => {
    if (!enabled)
      return noop
    let cancelled = false
    setLoading(true)
    setError(null)

    OrganizationIssuesService.getIssues({
      page,
      items,
      search,
      collectIds,
      steps,
      content,
      sort,
      direction,
      columns,
      variables,
      startDate,
      endDate
    })
      .then(response => {
        if (cancelled)
          return
        setIssues(response.issues || [])
        setStatsRows(response.stats || [])
        setStepRows(response.steps || [])
        setPagy(response.pagy || INITIAL_PAGY)
      })
      .catch(err => {
        if (cancelled)
          return
        setError(err)
        setIssues([])
        setStatsRows([])
        setStepRows([])
        setPagy(INITIAL_PAGY)
      })
      .finally(() => {
        if (cancelled)
          return
        setLoading(false)
        setHasLoaded(true)
      })

    return () => {
      cancelled = true
    }
    // Stable string keys avoid re-firing on array reference churn.
  }, [
    page,
    items,
    search,
    collectIdsKey,
    stepsKey,
    contentKey,
    sort,
    direction,
    columnsKey,
    variablesKey,
    startDate,
    endDate,
    enabled,
    refetchToken
  ])

  const groups = useMemo(() => groupByCategory((statsRows || []).map(toStatus)), [statsRows])

  return { issues, pagy, groups, steps: stepRows, loading, hasLoaded, error, refetch, mergeIssue }
}

export default useOrganizationAlerts
